import { useState } from 'react';
import { Clock, CheckCircle, Package, Banknote, CreditCard, UtensilsCrossed } from 'lucide-react';
import { useMarkReady, useCompleteOrder } from '../hooks/useQueueQuery';
import type { Order } from '../types';

interface OrderCardProps {
  order: Order;
  variant?: 'compact' | 'full';
  shopId: string;
}

export function OrderCard({ order, variant = 'compact', shopId }: OrderCardProps) {
  const markReadyMutation = useMarkReady(shopId);
  const completeOrderMutation = useCompleteOrder(shopId);
  const [error, setError] = useState<string | null>(null);

  const handleMarkReady = async () => {
    try {
      setError(null);
      await markReadyMutation.mutateAsync(order.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تحديث حالة الطلب');
    }
  };

  const handleComplete = async () => {
    try {
      setError(null);
      await completeOrderMutation.mutateAsync(order.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل إكمال الطلب');
    }
  };

  // Calculate time since order was created
  const getElapsedTime = () => {
    const created = new Date(order.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `${diffMins} د`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}س ${mins}د`;
  };

  // Status config
  const statusConfig = {
    confirmed: {
      label: 'مؤكد',
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
      borderColor: 'border-blue-400/30',
    },
    preparing: {
      label: 'قيد التحضير',
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
      borderColor: 'border-orange-400/30',
    },
    ready: {
      label: 'جاهز',
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      borderColor: 'border-green-400/30',
    },
  };

  const config = statusConfig[order.status as keyof typeof statusConfig];
  const isLoading = markReadyMutation.isPending || completeOrderMutation.isPending;

  if (variant === 'compact') {
    return (
      <div className={`rounded-xl border ${config?.borderColor || 'border-border'} ${config?.bgColor || 'bg-surface-light'} p-3`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white">#{order.queueNumber}</span>
            {config && (
              <span className={`text-xs ${config.color}`}>{config.label}</span>
            )}
            {/* Payment badge */}
            <span className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
              order.paymentMethod === 'card'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/20 text-amber-400'
            }`}>
              {order.paymentMethod === 'card' ? (
                <CreditCard className="h-3 w-3" />
              ) : (
                <Banknote className="h-3 w-3" />
              )}
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            <span>{getElapsedTime()}</span>
          </div>
        </div>

        {/* Items list for kitchen */}
        <div className="text-sm mb-3 space-y-2">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              {/* Small thumbnail */}
              <div className="w-8 h-8 rounded-lg bg-surface overflow-hidden shrink-0">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-surface">
                    <UtensilsCrossed className="h-4 w-4 text-gray-500" />
                  </div>
                )}
              </div>
              {/* Item details */}
              <div className="flex-1 min-w-0">
                <span className="text-white">{item.nameAr}</span>
                {(item.size || item.milk || item.variant) && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {item.size && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                        {item.size.nameAr}
                      </span>
                    )}
                    {item.variant && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                        {item.variant.nameAr}
                      </span>
                    )}
                    {item.milk && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                        {item.milk.nameAr}
                      </span>
                    )}
                  </div>
                )}
                {item.notes && (
                  <p className="text-xs text-gray-500 mt-0.5 italic">{item.notes}</p>
                )}
              </div>
              <span className="text-lg font-bold text-primary-400 shrink-0">×{item.quantity}</span>
            </div>
          ))}
          <div className="pt-1 border-t border-border/50 text-xs text-gray-500">
            المجموع: {parseInt(order.totalAmount).toLocaleString('ar-IQ')} د.ع
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-2 text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {(order.status === 'confirmed' || order.status === 'preparing') && (
            <button
              onClick={handleMarkReady}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-green-500 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {markReadyMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span>جاهز</span>
            </button>
          )}
          {order.status === 'ready' && (
            <button
              onClick={handleComplete}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-primary-500 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {completeOrderMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Package className="h-4 w-4" />
              )}
              <span>تم الاستلام</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div className={`rounded-2xl border ${config?.borderColor || 'border-border'} ${config?.bgColor || 'bg-surface-light'} p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold text-white">#{order.queueNumber}</span>
          {config && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color} ${config.bgColor}`}>
              {config.label}
            </span>
          )}
          {/* Payment badge */}
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            order.paymentMethod === 'card'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          }`}>
            {order.paymentMethod === 'card' ? (
              <>
                <CreditCard className="h-3 w-3" />
                <span>مدفوع</span>
              </>
            ) : (
              <>
                <Banknote className="h-3 w-3" />
                <span>نقدي</span>
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <Clock className="h-4 w-4" />
          <span>{getElapsedTime()}</span>
        </div>
      </div>

      {/* Customer info */}
      {order.customerName && (
        <p className="text-gray-400 mb-4">
          العميل: <span className="text-white">{order.customerName}</span>
        </p>
      )}

      {/* Items */}
      <div className="space-y-3 mb-4">
        {order.items.map((item, index) => (
          <div key={index} className="border-b border-border/50 pb-3 last:border-0 last:pb-0">
            <div className="flex items-start gap-3">
              {/* Item thumbnail */}
              <div className="w-10 h-10 rounded-xl bg-surface overflow-hidden shrink-0 border border-border/30">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-surface">
                    <UtensilsCrossed className="h-5 w-5 text-gray-500" />
                  </div>
                )}
              </div>
              {/* Item details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{item.nameAr}</span>
                    <span className="text-gray-500">×{item.quantity}</span>
                  </div>
                  <span className="text-gray-400">
                    {(item.unitPrice * item.quantity).toLocaleString('ar-IQ')} د.ع
                  </span>
                </div>
                {/* Item options: size, variant, milk */}
                {(item.size || item.variant || item.milk) && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {item.size && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                        {item.size.nameAr}
                      </span>
                    )}
                    {item.variant && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                        {item.variant.nameAr}
                      </span>
                    )}
                    {item.milk && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                        {item.milk.nameAr}
                      </span>
                    )}
                  </div>
                )}
                {/* Item notes */}
                {item.notes && (
                  <p className="text-xs text-gray-500 mt-1 italic">{item.notes}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flex justify-between border-t border-border pt-3">
        <span className="text-gray-400">المجموع</span>
        <span className="text-lg font-bold text-primary-500">
          {parseInt(order.totalAmount).toLocaleString('ar-IQ')} د.ع
        </span>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="mt-3 rounded-lg bg-surface p-2">
          <p className="text-xs text-gray-500">ملاحظات:</p>
          <p className="text-sm text-gray-300">{order.notes}</p>
        </div>
      )}
    </div>
  );
}
